/**
 * Path-based placeholder resolver for agent instructions and scheduled messages.
 *
 * Supports the full set documented in
 * `frontend/src/components/Settings/constants/placeholders.ts`:
 *
 *   {{now()}}, {{now().weekday}}, {{now().time_of_day}}, {{now().date}},
 *   {{now().time}}, {{now(America/New_York)}}
 *   {{week_horizon()}}, {{week_horizon(N)}}
 *   {{user_info()}}, {{user_info().user_name|.user_email|.user_phone|...}}
 *   {{user_name()}}
 *   {{current_conversation_id()}}
 *   {{previous_conversations(N)}}
 *   {{state()}}, {{state(conversation)}}, {{state(user)}}
 *   {{state(conversation, NAME)}}, {{state(user, NAME)}}, {{state(scope, NAME).field}}
 *   {{event_info()}}, {{event_info(scheduled)}}, {{event_info().event_name|...}}
 *   {{files()}}, {{files('kb')}}, {{files('skill')}}, {{files('kb','tree')}}
 *     (Markdown catalog; `tree` adds the canonical file paths under each entry)
 *   {{resources(...)}}  (deprecated alias of files(...), same arguments)
 *   {{file_content(PATH)}}  (raw text; caller pre-fetches into context.file_content)
 *   {{resource_content(NAME)}}  (deprecated; caller pre-fetches into context.resource_content)
 *   {{agent_settings(NAME)}}
 *   {{agent_skills()}}  (<available_skills> XML — the skill subset of context.resources)
 *   {{var(NAME)}}, {{var(NAME).field}}  (eval per-run variables; context.variables)
 *
 * Context is assembled by the caller (typically ConversationDO, which has all
 * this data locally). Unresolvable placeholders are returned unchanged so the
 * message isn't silently corrupted if context is missing.
 */

export interface PlaceholderUser {
  user_id?: string;
  user_name?: string;
  user_email?: string;
  user_phone?: string;
  user_language?: string;
  user_timezone?: string;
  [extra: string]: unknown;
}

export interface PlaceholderEventInfo {
  event_name?: string;
  event_date?: string; // ISO
  datetime_formatted?: string;
  days_until?: number;
  status?: string; // 'scheduled' | 'completed' | ...
  [extra: string]: unknown;
}

export interface PlaceholderResource {
  resource_id: string;
  name: string;
  resource_type: 'knowledge' | 'skill';
  /** Human description (knowledge: `resource_description`). */
  description?: string | null;
  /** Count of enabled files in the resource — rendered as "N files". */
  file_count?: number;
  /** Canonical file paths (`skills/<slug>/<relative-path>` for a skill, `resources/…` for knowledge). Populated by the
   * caller whenever this turn could render them; whether a given CALL renders
   * them is decided here, by the `tree` argument (or `structure_pinned`).
   * These are addresses, not labels — the file tools accept them verbatim, and
   * unlike ids they survive a publish. */
  structure?: string[];
  /**
   * Deprecation-window only: the agent's link carries the legacy
   * `include_structure_in_prompt` flag, so `structure` renders regardless of the
   * `tree` argument — but ONLY for the deprecated `{{resources(...)}}` spelling.
   *
   * Scoping it to the old name is what keeps the flag from becoming
   * inescapable: this release removes every surface that could clear the stored
   * column, so an author on such a hub would otherwise have no way to stop the
   * path listing. Renaming the call to `{{files(...)}}` is that way out, and it
   * leaves an untouched legacy template rendering exactly what it rendered
   * before. Removed with the column in PR 5b.
   */
  structure_pinned?: boolean;
}

export interface PlaceholderAgentSettings {
  agent_name: string;
  agent_role: string;
  instructions: string;
  [extra: string]: unknown;
}

export interface PlaceholderContext {
  /** Hub timezone (fallback when `now(TZ)` doesn't specify one). */
  timezone?: string;
  locale?: string;
  base_time?: string;
  /** Current conversation. */
  conversation?: {
    conversation_id?: string;
    kanban_status?: string;
    created_at?: string;
  };
  /** End user participating in the conversation. */
  user?: PlaceholderUser;
  /** Per-scope state, keyed by state_slug. `{{state(scope, slug)}}` reads state[scope][slug]. */
  state?: {
    conversation?: Record<string, unknown>;
    user?: Record<string, unknown>;
  };
  /** Previous closed conversations — newest first, each with `messages` summary. */
  previous_conversations?: Array<{
    conversation_id: string;
    summary?: string;
    ended_at?: string;
  }>;
  /** Event associated with the conversation (from kanban event_name + scheduled date). */
  event_info?: PlaceholderEventInfo;
  /** Knowledge + skill resources linked to the *current* agent (the one whose
   * template is being resolved). Feeds both `{{files()}}` (Markdown) and
   * `{{agent_skills()}}` (the skill subset, as XML) — one source, two views. */
  resources?: PlaceholderResource[];
  /** File text keyed by the path string the template used, verbatim —
   * pre-fetched by the caller for the paths a template requests via
   * `{{file_content(PATH)}}` (async R2 read can't happen inside the synchronous
   * resolver). Keyed by the raw argument rather than a normalized form so the
   * lookup here stays an exact map read; the caller owns path matching. */
  file_content?: Record<string, string>;
  /** Resource text content keyed by resource name — the deprecated
   * `{{resource_content(NAME)}}` surface, superseded by `file_content` addressing
   * a resource root by path. Kept working indefinitely. */
  resource_content?: Record<string, string>;
  /** Agent settings indexed by agent_name. */
  agent_settings?: Record<string, PlaceholderAgentSettings>;
  /**
   * Named, caller-supplied values for `{{var(NAME)}}` / `{{var(NAME).field}}`.
   * Used by eval scenarios: the caller resolves the per-run index up front and
   * passes the *already-indexed* row here (run i → `{ NAME: variables.NAME[i] }`),
   * so this map is index-free — `{{var(case)}}` reads `variables.case`.
   */
  variables?: Record<string, unknown>;
}

/** Hard cap for `{{previous_conversations(N)}}` — bounds per-turn token cost
 * when a hub author writes a large N. */
export const PREVIOUS_CONVERSATIONS_MAX = 20;

/**
 * The single rounding + bounding rule for `previous_conversations_count`.
 *
 * Every write surface needs it — REST via the agent contract's Zod field
 * (`contracts/setup/agents.ts`, which re-exports this), CI create and update
 * (which carry no Zod), and the settings input — and the READ side in ConversationDO
 * clamps again as defence in depth. Five independent expressions is how they end up
 * disagreeing about a fractional value, so there is one.
 *
 * Returns null for "off", which is what both 0 and null mean at the column.
 */
export function clampPreviousConversationsCount(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const rounded = Math.min(PREVIOUS_CONVERSATIONS_MAX, Math.max(0, Math.round(value)));
  return rounded > 0 ? rounded : null;
}

/** Default span and hard cap (in days, including today) for `{{week_horizon(N)}}`
 * — bounds per-turn token cost when a hub author writes a large N. */
export const WEEK_HORIZON_DEFAULT_DAYS = 8;
export const WEEK_HORIZON_MAX_DAYS = 31;

/** Returns `null` when the placeholder is absent — producer should skip the
 * index read in that case.
 *
 * The pre-scan decides whether to FETCH, so it must be at least as permissive as
 * the resolver that consumes the result — otherwise the producer skips the read,
 * `ctx.previous_conversations` stays unset, and the author sees either a raw
 * `{{...}}` or (worse, because it is indistinguishable from a user with no
 * history) an empty `[]`. Every `parseExpr` tolerance has to be mirrored here:
 * a trailing path (`{{previous_conversations(1).[0].summary}}`), whitespace before
 * the paren (`{{previous_conversations (3)}}`), and a quoted arg
 * (`{{previous_conversations('3')}}`, which `stripQuotes` unwraps). Same contract
 * as `scanTemplateResources` below, which satisfies it by walking `parseExpr`
 * itself rather than re-deriving the tolerances. */
export function parseMaxPreviousConversationsN(template: string | undefined): number | null {
  if (!template || !/previous_conversations\s*\(/.test(template)) return null;
  const re = /\{\{\s*previous_conversations\s*\(\s*['"]?(\d+)['"]?\s*\)\s*(?:\.[^{}]*)?\}\}/g;
  let max = 0;
  for (const m of template.matchAll(re)) {
    const n = parseInt(m[1], 10);
    if (Number.isFinite(n) && n > max) max = n;
  }
  if (max <= 0) return null;
  return Math.min(max, PREVIOUS_CONVERSATIONS_MAX);
}

/**
 * One placeholder expression, braces stripped. The single definition of "what
 * is a placeholder" — the resolver replaces on it and `parseCalls` walks it, so
 * a pre-scan cannot recognize a call the resolver won't, or miss one it will.
 * `g` is safe to share because both uses go through `replace`/`matchAll`, which
 * reset `lastIndex`.
 */
const PLACEHOLDER_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;

/** Options for `resolvePlaceholders` / `resolvePlaceholdersDeep`. */
export interface ResolvePlaceholderOptions {
  /**
   * Restrict resolution to this allowlist of function names — any other
   * `{{fn(...)}}` is left literal. Used by eval scenarios to resolve ONLY
   * `{{var()}}` and leave `{{now()}}`/`{{state()}}`/etc. untouched (those stay
   * literal in the frozen snapshot, preserving pre-variables behavior; `input`
   * still resolves them later at fire time via the conversation turn context).
   */
  only?: ReadonlySet<string>;
}

/**
 * Resolve all placeholders in `template` using `context`. Placeholders that
 * cannot be resolved are left literal. `options.only` restricts which function
 * names resolve (default: all).
 */
export function resolvePlaceholders(
  template: string,
  context: PlaceholderContext = {},
  options: ResolvePlaceholderOptions = {},
): string {
  if (!template || !template.includes('{{')) return template;

  return template.replace(PLACEHOLDER_RE, (match, raw: string) => {
    const resolved = resolveOne(raw.trim(), context, options.only);
    return resolved ?? match;
  });
}

/**
 * Recursively resolve placeholders in every string leaf of a JSON-like value
 * (objects, arrays, and strings), returning a new value with the same shape.
 * Non-string leaves pass through untouched. Used by eval scenarios, whose
 * fields (`input`, `expected`, `history`) are structured objects rather than a
 * single template string — e.g. `expected.tool_calls[].function.arguments` is a
 * JSON string carrying `{{var(case).patient_id}}`.
 */
export function resolvePlaceholdersDeep<T>(
  value: T,
  context: PlaceholderContext = {},
  options: ResolvePlaceholderOptions = {},
): T {
  if (typeof value === 'string') {
    return resolvePlaceholders(value, context, options) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map((v) => resolvePlaceholdersDeep(v, context, options)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = resolvePlaceholdersDeep(v, context, options);
    }
    return out as T;
  }
  return value;
}

/** Resolve a single placeholder expression (no surrounding braces). */
function resolveOne(expr: string, ctx: PlaceholderContext, only?: ReadonlySet<string>): string | null {
  const parsed = parseExpr(expr);
  if (!parsed) return null;

  const { fn, args, path } = parsed;
  // Allowlist gate — a function outside `only` is left literal (not resolved).
  if (only && !only.has(fn)) return null;
  const base = callFn(fn, args, ctx);
  if (base === null) return null;

  if (!path.length) return stringifyValue(base);
  const walked = walkPath(base, path);
  if (walked !== null) return stringifyValue(walked);
  // Missing subfield of a *present* base: eval `var()` resolves to empty string
  // (sparse per-run bundles — a row may legitimately omit a field), while other
  // functions stay literal so a bad path is visible to the author. An unknown
  // `var(NAME)` returns `base === null` above and also stays literal (typo signal).
  return fn === 'var' ? '' : null;
}

interface ParsedExpr {
  fn: string;
  args: string[];
  path: string[];
}

/** Parse "fn(a, b).a.b[0]" into { fn, args, path }. */
function parseExpr(expr: string): ParsedExpr | null {
  const fnMatch = expr.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*\((.*?)\)\s*(?:\.(.*))?$/);
  if (!fnMatch) return null;

  const [, fn, rawArgs, rawPath] = fnMatch;
  const args = splitArgs(rawArgs);

  const path = rawPath ? rawPath.split('.').flatMap((seg) => {
    const indexed: string[] = [];
    seg.replace(/([^\[\]]+)|\[(\d+)\]/g, (_, prop, idx) => {
      if (prop) indexed.push(prop);
      else if (idx !== undefined) indexed.push(idx);
      return '';
    });
    return indexed;
  }) : [];

  return { fn, args, path };
}

/** Split a call's raw argument text into stripped tokens. Shared with the
 * pre-scan gates below so a gate can never disagree with `parseExpr` about what
 * a call's arguments are — the exact way the structure gate drifted from the
 * resolver before. */
function splitArgs(rawArgs: string): string[] {
  const trimmed = rawArgs.trim();
  return trimmed ? trimmed.split(',').map((s) => stripQuotes(s.trim())) : [];
}

function stripQuotes(s: string): string {
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    return s.slice(1, -1);
  }
  return s;
}

function walkPath(base: unknown, path: string[]): unknown {
  let cur: unknown = base;
  for (const key of path) {
    if (cur == null) return null;
    if (Array.isArray(cur)) {
      const idx = parseInt(key, 10);
      cur = Number.isFinite(idx) ? cur[idx] : null;
    } else if (typeof cur === 'object') {
      cur = (cur as Record<string, unknown>)[key];
    } else {
      return null;
    }
  }
  return cur ?? null;
}

function stringifyValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

// ---------------------------------------------------------------------------
// Function dispatch
// ---------------------------------------------------------------------------

function callFn(fn: string, args: string[], ctx: PlaceholderContext): unknown {
  const arg0 = args[0] ?? null;
  switch (fn) {
    case 'now': {
      const hasExplicitTimezone = args.length > 0;
      return buildNow(
        hasExplicitTimezone ? arg0 || ctx.timezone : ctx.timezone,
        ctx.base_time,
        ctx.locale,
        hasExplicitTimezone ? ctx.timezone : undefined,
      );
    }
    case 'week_horizon':
      return buildWeekHorizon(parseHorizonDays(arg0), ctx.timezone);
    case USER_INFO_FN:
      return ctx.user ?? null;
    case USER_NAME_FN:
      return ctx.user?.user_name ?? null;
    case 'current_conversation_id':
      return ctx.conversation?.conversation_id ?? null;
    case 'previous_conversations':
      return takeN(ctx.previous_conversations, arg0);
    case 'state': {
      // Three forms:
      //   {{state()}}           → conversation-scope object (back-compat: prior
      //                            implementations only loaded conversation scope).
      //   {{state(scope)}}      → that scope's full object.
      //   {{state(scope, name)}} → individual state value (or null → literal).
      if (args.length === 0) return ctx.state?.conversation ?? {};
      if (args.length === 1) {
        const scope = arg0 as 'conversation' | 'user' | string;
        if (scope === 'conversation' || scope === 'user') return ctx.state?.[scope] ?? {};
        return null;
      }
      const scope = args[0] as 'conversation' | 'user' | string;
      const name = args[1];
      if ((scope !== 'conversation' && scope !== 'user') || !name) return null;
      return ctx.state?.[scope]?.[name] ?? null;
    }
    case 'event_info':
      // arg may be a status filter ("scheduled") — we currently return the same
      // event_info regardless; arg is documented but not acted on.
      return ctx.event_info ?? null;
    case 'files':
    case 'resources': {
      // Markdown catalog of the current agent's linked resources. Args are an
      // order-insensitive token set — kind ('kb' | 'skill') plus the optional
      // 'tree', which adds each file's canonical path. Returns a formatted
      // string (not the raw array) so it drops straight into the prompt — path
      // access isn't meaningful here. `resources` is the deprecated spelling of
      // the same call; both parse identically so a hub can migrate a name at a
      // time.
      const parsed = parseCatalogArgs(args);
      return formatResourcesMarkdown(
        filterResources(ctx.resources, parsed.kind),
        parsed,
        // Only the deprecated spelling still honors the legacy per-link flag.
        fn === LEGACY_CATALOG_FN,
      );
    }
    case 'file_content':
      // Keyed by the raw argument — the caller resolved the path and keyed the
      // map by exactly the string the template wrote.
      return arg0 ? ctx.file_content?.[arg0] ?? null : null;
    case 'resource_content':
      return arg0 ? ctx.resource_content?.[arg0] ?? null : null;
    case 'agent_settings':
      return arg0 ? ctx.agent_settings?.[arg0] ?? null : null;
    case 'agent_skills':
      // <available_skills> XML for the skill subset of the current agent's
      // linked resources — the progressive-disclosure surface documented in
      // references/agents/instructions.md.
      return formatAgentSkillsXml(filterResources(ctx.resources, 'skill'));
    case 'var':
      // `{{var(NAME)}}` — NAME is the (already index-resolved) row key. Missing
      // key → null → placeholder left literal (path access on it yields '').
      return arg0 ? ctx.variables?.[arg0] ?? null : null;
    default:
      return null;
  }
}

function takeN(list: unknown[] | undefined, arg: string | null): unknown[] {
  const n = arg ? parseInt(arg, 10) : 0;
  if (!list || !Number.isFinite(n) || n <= 0) return [];
  return list.slice(0, n);
}

/** Kind filter for the catalog placeholders; `null` = every linked resource. */
export type CatalogKind = 'kb' | 'skill';

/** The two spellings of the catalog placeholder. `resources` is the deprecated
 * one and the only spelling the legacy `structure_pinned` flag applies to. */
const CATALOG_FN = 'files';
const LEGACY_CATALOG_FN = 'resources';

/** The two placeholders that read the person — named once for the resolver and its gate. */
const USER_NAME_FN = 'user_name';
const USER_INFO_FN = 'user_info';

interface CatalogArgs {
  kind: CatalogKind | null;
  /** `tree` token present — render each resource's canonical file paths. */
  tree: boolean;
}

/**
 * `{{files(...)}}` / `{{resources(...)}}` arguments as an order-insensitive
 * token set: `files('kb','tree')` and `files('tree','kb')` are the same call.
 * Tokens match exactly (no case folding) — `{{resources(KB)}}` has always meant
 * "no kind filter", and this parser must not silently start filtering it.
 * Unrecognized tokens are ignored, as before.
 */
function parseCatalogArgs(args: string[]): CatalogArgs {
  let kind: CatalogKind | null = null;
  let tree = false;
  for (const token of args) {
    if (token === 'tree') tree = true;
    else if ((token === 'kb' || token === 'skill') && kind === null) kind = token;
  }
  return { kind, tree };
}

function filterResources(resources: PlaceholderResource[] | undefined, kind: CatalogKind | null): PlaceholderResource[] {
  if (!resources) return [];
  if (!kind) return resources;
  if (kind === 'kb') return resources.filter((r) => r.resource_type === 'knowledge');
  return resources.filter((r) => r.resource_type === 'skill');
}

// ---------------------------------------------------------------------------
// resources() / agent_skills() formatters
//
// filterResources returns the raw PlaceholderResource[]; these turn it into the
// prompt-ready shapes documented in references/agents/instructions.md. Kept pure
// (no I/O) — the caller pre-computes file_count / structure / content and threads
// them onto the context, mirroring how state/kanban slugs are pre-resolved.
// ---------------------------------------------------------------------------

/** Empty-catalog note, kind-aware so a `{{files(kb)}}` on a skills-only agent
 * reads honestly instead of implying nothing at all is linked. */
function emptyResourcesLabel(kind: CatalogKind | null): string {
  if (kind === 'kb') return '(no knowledge resources are linked to this agent)';
  if (kind === 'skill') return '(no skills are linked to this agent)';
  return '(no resources are linked to this agent)';
}

/**
 * Markdown block for `{{files()}}` — name, type, id, description, file count,
 * and, when this CALL asked for it, the canonical file paths.
 *
 * Structure gating lives here rather than in the caller because it is per-call:
 * one template may render `{{files('kb')}}` and `{{files('skill','tree')}}` and
 * get structure in only the second. `honorPinned` is the deprecation-window
 * override — true only for the legacy `{{resources(...)}}` spelling, so a hub
 * carrying the retired `include_structure_in_prompt` flag keeps its old prompt
 * while `{{files(...)}}` obeys its arguments alone. It and `structure_pinned`
 * disappear together in PR 5b.
 */
function formatResourcesMarkdown(
  resources: PlaceholderResource[],
  { kind, tree }: CatalogArgs,
  honorPinned: boolean,
): string {
  if (resources.length === 0) return emptyResourcesLabel(kind);
  const lines: string[] = [];
  for (const r of resources) {
    const typeLabel = r.resource_type === 'skill' ? 'skill' : 'knowledge base';
    let header = `- **${r.name || 'unnamed'}** (${typeLabel}) — id: \`${r.resource_id}\``;
    if (typeof r.file_count === 'number') {
      header += `, ${r.file_count} file${r.file_count === 1 ? '' : 's'}`;
    }
    lines.push(header);
    if (r.description) lines.push(`  ${r.description}`);
    if ((tree || (honorPinned && r.structure_pinned)) && r.structure && r.structure.length > 0) {
      for (const path of r.structure) lines.push(`    - ${path}`);
    }
  }
  return lines.join('\n');
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** `<available_skills>` XML for `{{agent_skills()}}` — one `<skill>` per linked
 * skill resource. Empty stays as an empty element so the block is still valid XML
 * the model can reason about ("no skills available"). */
function formatAgentSkillsXml(skills: PlaceholderResource[]): string {
  if (skills.length === 0) return '<available_skills></available_skills>';
  const parts = ['<available_skills>'];
  for (const s of skills) {
    parts.push('<skill>');
    parts.push(`  <skill_id>${escapeXml(s.resource_id)}</skill_id>`);
    parts.push(`  <name>${escapeXml(s.name || '')}</name>`);
    if (s.description) parts.push(`  <description>${escapeXml(s.description)}</description>`);
    parts.push('</skill>');
  }
  parts.push('</available_skills>');
  return parts.join('\n');
}

// ---------------------------------------------------------------------------
// Pre-scan
//
// The producer decides what to FETCH from the template alone, before the
// resolver runs, so a scan that disagrees with the resolver about what a
// template contains is the recurring bug in this module: the fetch is skipped,
// the context stays unset, and the author sees a raw `{{...}}` — or, worse, an
// honest-looking but empty block.
//
// The scan therefore does not pattern-match the template a second time. It
// walks the SAME outer expression regex `resolvePlaceholders` uses and runs each
// match through the SAME `parseExpr`, so gate and resolver agree by
// construction rather than by review. Hand-rolled variants drifted three ways
// before this: an argument capture that could not contain `)` but could contain
// `}` swallowed past a call's own closing braces (a malformed earlier call then
// hid a well-formed later one); capturing the argument text whole disagreed
// with `splitArgs` about a comma; and `.` in `parseExpr` not matching newlines
// made a multi-line call resolve as literal while the gate fetched for it.
// ---------------------------------------------------------------------------

/** Collapse the kinds seen across a template's calls: any unfiltered call (or
 * calls covering both kinds) widens to every resource. */
function widenKinds(kinds: Set<CatalogKind | 'all'>): 'all' | CatalogKind | null {
  if (kinds.has('all') || kinds.size > 1) return 'all';
  return ([...kinds][0] as CatalogKind | undefined) ?? null;
}

/** Every well-formed placeholder call in `template`, parsed exactly as the
 * resolver parses it. Malformed calls yield nothing here for the same reason
 * they render literally there. */
function* parseCalls(template: string): Generator<ParsedExpr> {
  for (const match of template.matchAll(PLACEHOLDER_RE)) {
    const parsed = parseExpr(match[1].trim());
    if (parsed) yield parsed;
  }
}

/** What a template asks its resource placeholders for. One scan answers every
 * question the producer has, so the template is walked once per turn. */
export interface TemplateResourceScan {
  /** `{{files()}}`, `{{resources()}}` or `{{agent_skills()}}` is present — the
   * agent-scoped catalog has to be built. */
  needs_catalog: boolean;
  /** A `{{files()}}` / `{{resources()}}` call is present. `{{agent_skills()}}`
   * is deliberately NOT one: it renders no file paths, so a template using only
   * it needs no canonical paths derived and no deprecation notice. */
  has_catalog_call: boolean;
  /** The kinds the DEPRECATED `{{resources()}}` calls select — the only
   * spelling the legacy `structure_pinned` flag still applies to. null when no
   * such call is present. Gone in PR 5b. */
  legacy_kinds: 'all' | CatalogKind | null;
  /** The kinds some call asked a `tree` for: `'all'` when an unfiltered call
   * did (or calls between them covered both), else the one kind, else null.
   *
   * Both fields are kind-aware because the resolver filters by kind BEFORE it
   * reads `structure`, so paths derived for the other kind can never render —
   * deriving them is a folder RPC per resource per turn, and (for the legacy
   * field) a false positive in the signal that gates dropping the column. */
  tree_kinds: 'all' | CatalogKind | null;
  /** Distinct `{{resource_content(NAME)}}` names. */
  resource_content_names: string[];
  /** Distinct `{{file_content(PATH)}}` paths, exactly as the resolver will look
   * them up — `context.file_content` is keyed by this string. */
  file_content_paths: string[];
}

const EMPTY_SCAN: TemplateResourceScan = {
  needs_catalog: false,
  has_catalog_call: false,
  legacy_kinds: null,
  tree_kinds: null,
  resource_content_names: [],
  file_content_paths: [],
};

/**
 * Scan a template for every resource placeholder the producer must pre-fetch
 * for. Returns the all-false/empty result when the template has no placeholders
 * at all, so the common agent pays one `includes('{{')` and nothing more.
 */
export function scanTemplateResources(template: string | undefined): TemplateResourceScan {
  if (!template || !template.includes('{{')) return EMPTY_SCAN;

  const scan: TemplateResourceScan = { ...EMPTY_SCAN };
  const names = new Set<string>();
  const paths = new Set<string>();
  const treeKinds = new Set<CatalogKind | 'all'>();
  const legacyKinds = new Set<CatalogKind | 'all'>();

  for (const { fn, args } of parseCalls(template)) {
    switch (fn) {
      case CATALOG_FN:
      case LEGACY_CATALOG_FN: {
        scan.needs_catalog = true;
        scan.has_catalog_call = true;
        const { kind, tree } = parseCatalogArgs(args);
        if (fn === LEGACY_CATALOG_FN) legacyKinds.add(kind ?? 'all');
        if (tree) treeKinds.add(kind ?? 'all');
        break;
      }
      case 'agent_skills':
        scan.needs_catalog = true;
        break;
      case 'resource_content':
        if (args[0]) names.add(args[0]);
        break;
      case 'file_content':
        if (args[0]) paths.add(args[0]);
        break;
      default:
        break;
    }
  }

  scan.tree_kinds = widenKinds(treeKinds);
  scan.legacy_kinds = widenKinds(legacyKinds);
  scan.resource_content_names = [...names];
  scan.file_content_paths = [...paths];
  return scan;
}

/**
 * Whether `template` calls `{{user_name()}}` or `{{user_info()}}` — the producer's gate for
 * reading the person's profile. Walks `parseCalls`, so it recognizes exactly the calls the
 * resolver resolves (the contract `parseMaxPreviousConversationsN` states).
 */
export function templateNamesUser(template: string | undefined): boolean {
  if (!template || !template.includes('{{')) return false;
  for (const { fn } of parseCalls(template)) {
    if (fn === USER_NAME_FN || fn === USER_INFO_FN) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// now() helpers
// ---------------------------------------------------------------------------

interface NowResult {
  iso: string;
  weekday: string;
  time_of_day: string;
  date: string;
  time: string;
  [extra: string]: unknown;
}

function buildNow(
  timezone?: string | null,
  baseTime?: string,
  locale?: string,
  fallbackTimezone?: string | null,
): NowResult {
  const parsedBase = baseTime ? new Date(baseTime) : null;
  const now = parsedBase && !Number.isNaN(parsedBase.getTime()) ? parsedBase : new Date();

  const parts = makeIntl(locale, timezone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'long',
  }, fallbackTimezone).formatToParts(now);

  const year = pickPart(parts, 'year');
  const month = pickPart(parts, 'month');
  const day = pickPart(parts, 'day');
  let hourStr = pickPart(parts, 'hour');
  if (hourStr === '24') hourStr = '00';
  const minute = pickPart(parts, 'minute');
  const weekday = pickPart(parts, 'weekday');

  const hour = parseInt(hourStr, 10);
  const result: NowResult = {
    iso: now.toISOString(),
    weekday,
    time_of_day: partOfDay(hour),
    date: `${year}-${month}-${day}`,
    time: `${hourStr}:${minute}`,
  };
  // Default stringification of the object returns the ISO string for the common
  // `{{now()}}` usage via JSON fallback. Provide a toString override for clarity.
  Object.defineProperty(result, 'toString', { value: () => result.iso });
  return result;
}

function partOfDay(hour: number): string {
  if (hour < 5) return 'night';
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  if (hour < 21) return 'evening';
  return 'night';
}

/** Build a formatter, falling back through locale and timezone independently. */
function makeIntl(
  locale: string | null | undefined,
  timeZone: string | null | undefined,
  options: Intl.DateTimeFormatOptions,
  fallbackTimeZone?: string | null,
): Intl.DateTimeFormat {
  const candidates = [
    [locale || 'en-US', timeZone], ['en-US', timeZone],
    [locale || 'en-US', fallbackTimeZone], ['en-US', fallbackTimeZone],
  ] as const;
  for (const [candidateLocale, candidateTimezone] of candidates) {
    try { return new Intl.DateTimeFormat(candidateLocale, { timeZone: candidateTimezone || undefined, ...options }); }
    catch { continue; }
  }
  return new Intl.DateTimeFormat('en-US', options);
}

/** Extract a formatted part by type from `formatToParts` output. */
function pickPart(parts: Intl.DateTimeFormatPart[], type: string): string {
  return parts.find((p) => p.type === type)?.value ?? '';
}

// ---------------------------------------------------------------------------
// week_horizon() helpers
// ---------------------------------------------------------------------------

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/** Parse the day-count arg; default on missing/invalid/≤0, clamp to the cap. */
function parseHorizonDays(arg: string | null): number {
  const n = arg ? parseInt(arg, 10) : NaN;
  if (!Number.isFinite(n) || n <= 0) return WEEK_HORIZON_DEFAULT_DAYS;
  return Math.min(n, WEEK_HORIZON_MAX_DAYS);
}

/**
 * Rolling `date → weekday` table for the next `days` calendar days (including
 * today), in the hub timezone. The model looks up the weekday instead of
 * deriving it from a date — which LLMs do unreliably.
 *
 * Weekday is read from the calendar date via `getUTCDay()` on a UTC-anchored
 * value, so no instant→tz conversion can shift the day; the timezone only
 * selects which calendar day is "today".
 */
function buildWeekHorizon(days: number, timezone?: string | null): string {
  const parts = makeIntl(undefined, timezone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const year = parseInt(pickPart(parts, 'year'), 10);
  const month = parseInt(pickPart(parts, 'month'), 10);
  const day = parseInt(pickPart(parts, 'day'), 10);

  const base = Date.UTC(year, month - 1, day);
  const lines: string[] = [];
  for (let i = 0; i < days; i++) {
    const d = new Date(base + i * 86_400_000);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const weekday = WEEKDAY_NAMES[d.getUTCDay()];
    const label = i === 0 ? 'today: ' : i === 1 ? 'tomorrow: ' : '';
    lines.push(`${label}${y}-${m}-${dd}/${weekday}`);
  }
  return lines.join('\n');
}
